import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { supportAPI } from '../../utils/api';
import {
  Mail, Shield, Bug, MessageSquare, AlertTriangle, Sparkles, RefreshCw,
} from 'lucide-react';

const CATEGORY_META = {
  general: { tr: 'Genel', Icon: MessageSquare, color: 'bg-stone-100 text-stone-700' },
  kvkk: { tr: 'KVKK', Icon: Shield, color: 'bg-teal-100 text-teal-800' },
  bug: { tr: 'Hata', Icon: Bug, color: 'bg-rose-100 text-rose-800' },
  feedback: { tr: 'Geri Bildirim', Icon: Sparkles, color: 'bg-amber-100 text-amber-800' },
  abuse: { tr: 'Kötüye Kullanım', Icon: AlertTriangle, color: 'bg-orange-100 text-orange-800' },
};
const STATUS_META = {
  new: { tr: 'Yeni', color: 'bg-blue-100 text-blue-800' },
  in_progress: { tr: 'İşlemde', color: 'bg-amber-100 text-amber-800' },
  resolved: { tr: 'Yanıtlandı', color: 'bg-emerald-100 text-emerald-800' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const SupportInbox = () => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [selected, setSelected] = useState(null);
  const [savingNote, setSavingNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await supportAPI.list(filterStatus || undefined, filterCategory || undefined);
      setItems(r.data?.items || []);
      setCounts(r.data?.counts || {});
    } catch {
      toast.error('Destek mesajları yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterCategory]);

  useEffect(() => { load(); }, [load]);

  const openMessage = useCallback((m) => {
    setSelected(m);
    setNoteDraft(m.admin_note || '');
  }, []);

  const updateMessage = useCallback(async (id, body) => {
    setSavingNote(true);
    try {
      await supportAPI.update(id, body);
      toast.success('Güncellendi');
      setItems((arr) => arr.map((it) => (it.id === id ? { ...it, ...body } : it)));
      setSelected((s) => (s && s.id === id ? { ...s, ...body } : s));
      load();
    } catch {
      toast.error('Güncelleme başarısız');
    } finally {
      setSavingNote(false);
    }
  }, [load]);

  return (
    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden shadow-sm mb-8" data-testid="admin-support-inbox">
      <div className="px-6 py-4 border-b border-stone-200 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <div className="flex items-center gap-3">
          <Mail className="w-5 h-5 text-teal-700" />
          <h2 className="text-lg font-bold text-stone-900 font-elegant">Destek Mesajları</h2>
          <div className="flex items-center gap-2 text-xs">
            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-semibold">{counts.new || 0} yeni</span>
            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">{counts.in_progress || 0} işlemde</span>
            <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">{counts.resolved || 0} kapalı</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="text-sm border border-stone-200 rounded-lg px-3 py-2 bg-white"
            data-testid="support-filter-status"
          >
            <option value="">Tüm durumlar</option>
            <option value="new">Yeni</option>
            <option value="in_progress">İşlemde</option>
            <option value="resolved">Yanıtlandı</option>
          </select>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="text-sm border border-stone-200 rounded-lg px-3 py-2 bg-white"
            data-testid="support-filter-category"
          >
            <option value="">Tüm kategoriler</option>
            <option value="general">Genel</option>
            <option value="kvkk">KVKK</option>
            <option value="bug">Hata</option>
            <option value="feedback">Geri Bildirim</option>
            <option value="abuse">Kötüye Kullanım</option>
          </select>
          <button type="button" onClick={load} className="p-2 rounded-lg hover:bg-stone-100" title="Yenile" data-testid="support-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 divide-x divide-stone-200">
        <div className="max-h-[60vh] overflow-y-auto">
          {loading && items.length === 0 ? (
            <div className="p-8 text-center text-stone-400 text-sm">Yükleniyor…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-stone-400 text-sm">
              <Mail className="w-10 h-10 mx-auto mb-2 opacity-40" />
              Henüz destek mesajı yok.
            </div>
          ) : items.map((m) => {
            const cat = CATEGORY_META[m.category] || CATEGORY_META.general;
            const st = STATUS_META[m.status] || STATUS_META.new;
            const Icon = cat.Icon;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => openMessage(m)}
                data-testid={`support-row-${m.id}`}
                className={`w-full text-left p-4 border-b border-stone-100 hover:bg-stone-50 transition-colors ${selected?.id === m.id ? 'bg-teal-50/50' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${cat.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold text-stone-900 truncate">{m.subject}</p>
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${st.color}`}>{st.tr}</span>
                    </div>
                    <p className="text-xs text-stone-500 truncate">{m.name} · {m.email}</p>
                    <p className="text-xs text-stone-400">{formatDate(m.created_at)}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="p-5 max-h-[60vh] overflow-y-auto">
          {!selected ? (
            <div className="text-center text-stone-400 text-sm py-16">Bir mesaj seçin</div>
          ) : (
            <div className="space-y-4" data-testid="support-detail">
              <header className="space-y-2 border-b border-stone-200 pb-3">
                <h3 className="text-lg font-bold text-stone-900">{selected.subject}</h3>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className={`px-2 py-0.5 rounded-full font-semibold ${(CATEGORY_META[selected.category] || CATEGORY_META.general).color}`}>
                    {(CATEGORY_META[selected.category] || CATEGORY_META.general).tr}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full font-semibold ${(STATUS_META[selected.status] || STATUS_META.new).color}`}>
                    {(STATUS_META[selected.status] || STATUS_META.new).tr}
                  </span>
                  <span className="text-stone-500">{formatDate(selected.created_at)}</span>
                </div>
                <div className="text-xs text-stone-600">
                  <strong>{selected.name}</strong> · <a href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`} className="text-teal-700 underline" data-testid="support-reply-mailto">{selected.email}</a>
                </div>
              </header>

              <div className="bg-stone-50 rounded-xl p-4 whitespace-pre-wrap text-sm text-stone-800 leading-relaxed" data-testid="support-message-body">
                {selected.message}
              </div>

              <div className="space-y-2">
                <label className="block text-xs font-semibold text-stone-700">Yönetici notu (sadece sizin için)</label>
                <textarea
                  rows={3}
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  className="w-full p-2 rounded-lg border border-stone-200 text-sm outline-none focus:border-teal-500"
                  placeholder="Bu mesajla ilgili notunuz…"
                  data-testid="support-note-input"
                />
                <button
                  type="button"
                  onClick={() => updateMessage(selected.id, { admin_note: noteDraft })}
                  disabled={savingNote}
                  className="px-3 py-1.5 rounded-lg bg-stone-700 hover:bg-stone-800 text-white text-xs font-semibold"
                  data-testid="support-save-note"
                >Notu kaydet</button>
              </div>

              <div className="flex items-center gap-2 pt-2 border-t border-stone-200">
                <span className="text-xs text-stone-500 mr-auto">Durumu değiştir:</span>
                {Object.entries(STATUS_META).map(([id, m]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => updateMessage(selected.id, { status: id })}
                    data-testid={`support-status-${id}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${selected.status === id ? m.color + ' border-transparent' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'}`}
                  >
                    {m.tr}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportInbox;
